import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import { Handshake, ArrowRight, Warning, CircleNotch, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";

/*
  AcceptInvitePage — public landing for /accept-invite/:token
  1. Looks up the invite (no auth needed) to show the details
  2. If the caller is logged in AS the invited email → auto-accept
  3. If not logged in → prompt to sign up / log in with the invited email
*/

export default function AcceptInvitePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get(`/invite/${token}`);
        setInvite(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "This invite is not valid or has already been used.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const emailMatches = user && invite && (user.email || "").toLowerCase() === invite.invited_email.toLowerCase();

  const accept = async () => {
    if (!user) {
      // Bounce to signup with the invited email pre-filled + redirect back here
      try { sessionStorage.setItem("pending_invite_token", token); } catch {}
      nav(`/signup?invite_email=${encodeURIComponent(invite.invited_email)}`);
      return;
    }
    setAccepting(true);
    try {
      await client.post(`/invite/${token}/accept`);
      toast.success(`Welcome to ${invite.company_name}!`);
      // Force a hard refresh so the AuthContext reloads with the new membership
      window.location.href = "/app/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept invite");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <div className="text-center py-12 text-neutral-500 text-sm" data-testid="accept-loading">
          <CircleNotch size={20} className="animate-spin mx-auto mb-3" />
          Loading invite…
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div className="text-center py-8" data-testid="accept-error">
          <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
            <Warning size={22} className="text-red-400" weight="fill" />
          </div>
          <h1 className="font-display text-2xl font-semibold mb-2">Invite unavailable</h1>
          <p className="text-neutral-400 text-sm mb-6">{error}</p>
          <Link to="/" className="text-[#FF5A1F] hover:text-[#FF7038] text-sm font-medium">
            Back to homepage
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="text-center mb-8" data-testid="accept-details">
        <div className="w-14 h-14 rounded-full bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 flex items-center justify-center mx-auto mb-4">
          <Handshake size={22} className="text-[#FF5A1F]" weight="fill" />
        </div>
        <div className="overline mb-2">YOU'VE BEEN INVITED</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-3">
          Join <span className="text-[#FF5A1F]">{invite.company_name}</span>
        </h1>
        <p className="text-neutral-400 text-sm leading-relaxed max-w-md mx-auto">
          {invite.invited_by_name} invited you to join {invite.company_name} on FreightPilot AI as a{" "}
          <span className="text-white font-medium">{invite.role_label}</span>.
        </p>
      </div>

      {/* Invite details card */}
      <div className="card-surface p-5 sm:p-6 mb-6 space-y-3">
        <DetailRow label="Company" value={invite.company_name} />
        <DetailRow label="Your role" value={invite.role_label} highlight />
        <DetailRow label="Invited email" value={invite.invited_email} icon={<EnvelopeSimple size={12} />} />
      </div>

      {/* CTA */}
      {!user ? (
        <div className="space-y-3" data-testid="accept-not-logged-in">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-xs text-neutral-300 leading-relaxed">
            <div className="font-semibold text-amber-300 mb-1 uppercase tracking-wide">Log in or sign up to accept</div>
            You'll need to use the email <span className="font-mono text-white">{invite.invited_email}</span> when you create your account.
          </div>
          <button
            onClick={accept}
            data-testid="accept-signup-btn"
            className="w-full btn-primary rounded-full py-3 text-sm inline-flex items-center justify-center gap-2"
          >
            Sign up with {invite.invited_email} <ArrowRight size={14} weight="bold" />
          </button>
          <Link
            to={`/login?next=${encodeURIComponent(`/accept-invite/${token}`)}`}
            data-testid="accept-login-link"
            className="block text-center text-sm text-neutral-400 hover:text-white transition-colors py-2"
          >
            Already have an account? Log in
          </Link>
        </div>
      ) : !emailMatches ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-xs text-neutral-300 leading-relaxed" data-testid="accept-wrong-email">
          <div className="font-semibold text-red-300 mb-1 uppercase tracking-wide flex items-center gap-2">
            <Warning size={12} weight="fill" /> Wrong account
          </div>
          You're logged in as <span className="font-mono text-white">{user.email}</span>, but this invite was sent to{" "}
          <span className="font-mono text-white">{invite.invited_email}</span>. Please log out and sign in with the invited email.
        </div>
      ) : (
        <button
          onClick={accept}
          disabled={accepting}
          data-testid="accept-btn"
          className="w-full btn-primary rounded-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {accepting ? "Accepting…" : `Accept invite & join ${invite.company_name}`}
          <ArrowRight size={14} weight="bold" />
        </button>
      )}
    </PageShell>
  );
}

function DetailRow({ label, value, highlight, icon }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{label}</div>
      <div className={`text-sm inline-flex items-center gap-2 ${highlight ? "text-[#FF9764] font-medium" : "text-white"}`}>
        {icon && <span className="text-neutral-500">{icon}</span>}
        {value}
      </div>
    </div>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        {children}
      </div>
    </div>
  );
}
